import { useEffect, useRef } from 'react';
import { useMatch } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSessionClaims } from '@/auth/session-claims';
import type { DisruptionDetail } from '@/lib/disruptions/types';
import { ME_QUERY_KEY, type OperatorMe } from '@/lib/me/api';
import { queryClient } from '@/lib/query-client';
import { readAlerts } from '@/lib/alerts/alerts.api';
import { openAlertsChannel } from '@/lib/alerts/alerts.channel';
import {
  catchUp,
  markRead,
  openSummary,
  setAlertsRuntime,
  useAlertsStore,
} from '@/lib/alerts/alerts.store';
import { useConnectionStore } from '@/state/connection.store';
import { usePresenceStore, watchPresence } from '@/state/presence.store';
import { alertLine } from './alert-line';

// The operator-alert HOST (docs/verticals/operator-notifications.md slice U1, §7.5),
// mounted once in the AppShell. It owns the runtime the pure policy reads at event time:
//   · presence — `state/presence.store.ts` (hidden OR 2 min without input = away);
//   · focus — «Operar» (`/disruptions/:urn/operar`) with a disruption on screen, and that
//     disruption's flights as the TanStack cache already holds them (no read of its own);
//   · leader — the operator's most recently focused tab (`alerts.channel.ts`);
//   · me — `/v1/me` (the token's claim until it is read), so the operator's own
//     declarations never alert them.
// And the side effects: ONE catch-up `GET /v1/alerts?since=` on a socket RE-connect and
// on a return from away (never on a timer); the return summary (when this tab leads);
// the tab title's count while hidden («(3) Nexa», restored on return); the polite live
// region the quiet deliveries speak through (the `WorkAnnouncer` pattern); and every
// acknowledgement → `POST /v1/alerts/read` + the other tabs' counters.
// It reads the app's query-client singleton (like `lib/ws/socket.ts`), not a hook: the
// shell itself runs no query, and renders without a provider in its own tests.

function flightsOf(urn: string): string[] {
  const d = queryClient.getQueryData<DisruptionDetail>(['disruptions', urn]);
  const ids = [...(d?.scope?.flightUrns ?? []), ...(d?.cases ?? []).map((c) => c.flightUrn)];
  return ids.filter((f): f is string => !!f);
}

export function AlertsHost() {
  const { t } = useTranslation('alerts');
  const claimed = useSessionClaims()?.operatorUrn ?? undefined;
  const match = useMatch({ path: '/disruptions/:disruptionUrn/operar', end: false });
  const raw = match?.params.disruptionUrn;
  const focus = raw ? decodeURIComponent(raw) : null;
  const live = useRef(focus);
  useEffect(() => {
    live.current = focus;
  }, [focus]);

  useEffect(() => watchPresence(), []);

  useEffect(() => {
    const channel = openAlertsChannel(`nexa.alerts:${claimed ?? ''}`, markRead);
    // The return summary opens in the tab the operator came back to AND that leads; a
    // tab that comes back without leading keeps it pending until it is focused.
    const summon = (): void => {
      const s = useAlertsStore.getState();
      const present = usePresenceStore.getState().presence === 'present';
      if (s.awayCount > 0 && !s.summary && present && channel.leader()) openSummary('return');
    };
    setAlertsRuntime({
      context: () => {
        const p = usePresenceStore.getState();
        const urn = live.current;
        const away = p.presence === 'away';
        const me = queryClient.getQueryData<OperatorMe>(ME_QUERY_KEY)?.operatorUrn ?? claimed;
        return {
          ...(me ? { me } : {}),
          away,
          ...(away ? { awaySince: p.since } : {}),
          focus: urn ? { disruptionUrn: urn, flightUrns: flightsOf(urn) } : null,
          leader: channel.leader(),
        };
      },
      acknowledged: (upTo) => {
        channel.read(upTo);
        readAlerts(upTo).catch(() => undefined);
      },
    });
    const offPresence = usePresenceStore.subscribe((p, prev) => {
      if (prev.presence !== 'away' || p.presence !== 'present') return;
      summon();
      void catchUp(prev.since).then(summon);
    });
    // A RE-connect: pushes were missed meanwhile. The first connect is not a catch-up.
    let ever = useConnectionStore.getState().connected;
    const offSocket = useConnectionStore.subscribe((c, prev) => {
      if (!c.connected || prev.connected) return;
      if (ever) void catchUp();
      ever = true;
    });
    window.addEventListener('focus', summon);
    return () => {
      setAlertsRuntime(null);
      channel.close();
      offPresence();
      offSocket();
      window.removeEventListener('focus', summon);
    };
  }, [claimed]);

  // While hidden, the tab title carries what queued meanwhile; shown again, it is restored.
  useEffect(() => {
    const base = document.title;
    const paint = (): void => {
      const n = useAlertsStore.getState().awayCount;
      document.title = document.visibilityState === 'hidden' && n > 0 ? `(${n}) ${base}` : base;
    };
    const off = useAlertsStore.subscribe(paint);
    document.addEventListener('visibilitychange', paint);
    return () => {
      off();
      document.removeEventListener('visibilitychange', paint);
      document.title = base;
    };
  }, []);

  const said = useAlertsStore((s) => s.entries.find((e) => e.alert.id === s.said)?.alert);
  return (
    <p className="sr-only" aria-live="polite" data-testid="alerts-announce">
      {said ? alertLine(t, said) : ''}
    </p>
  );
}
